import React from "react";
import { useStore } from "store";
import type { TourStep } from "./TourProvider";

/** Helper: returns true if the selector is NOT found in the DOM */
const notInDOM = (selector: string) => () => !document.querySelector(selector);

/** Helper: closes any open tool popover in BottomToolPanel */
const closePopover = () => {
  window.dispatchEvent(new CustomEvent("tour:close-popover"));
};

/** Helper: closes any open MUI Select dropdown by clicking its backdrop */
const closeDropdown = () => {
  const backdrop = document.querySelector(".MuiPopover-root > div[style*='position: fixed']") as HTMLElement;
  if (backdrop) {
    backdrop.click();
  } else {
    // Fallback: click body to close any open popover
    document.body.click();
  }
};

/** Helper: deselects all shapes, exits edit mode, and switches to select tool */
const deselectAll = () => {
  const editor = (window as any).__tldrawEditorForFontFix;
  if (editor) {
    // Exit edit mode first if active (e.g., text editing)
    if (editor.getEditingShapeId()) {
      editor.complete(); // Finishes the current edit operation
    }
    editor.setCurrentTool("select");
    editor.selectNone();
  }
};

export const flyerBuilderTourSteps: TourStep[] = [
  {
    targetSelector: '[data-tour="format"]',
    title: "Choose Flyer Format",
    description: "Select the size or layout that best fits your design needs.",
    placement: "right",
    offset: 16,
    hideSkip: true,
    disableNext: () => {
      try {
        return !(useStore.getState() as any).campaign?.flyerType;
      } catch {
        return true;
      }
    },
    onEnter: () => {
      // Ensure Design (Settings) tab is open
      const settingsTab = document.querySelector('[data-tour="sidebar-settings"]');
      const button =
        settingsTab?.querySelector("button") ||
        settingsTab?.querySelector("[role=button]") ||
        (settingsTab?.firstElementChild as HTMLElement);
      if (button) (button as HTMLElement).click();
    },
    onExit: closeDropdown,
    advanceOn: {
      selector: "body",
      event: "tour:format-selected",
    },
  },
  {
    targetSelector: '[data-tour="orientation"]',
    title: "Select Orientation",
    description: "Pick portrait or landscape to set the foundation of your design.",
    placement: "right",
    offset: 16,
    onExit: closeDropdown,
    skipIf: notInDOM('[data-tour="orientation"]'),
    advanceOn: {
      selector: "body",
      event: "tour:orientation-selected",
    },
  },
  {
    targetSelector: '[data-tour="address-box"]',
    title: "Add the Address Box",
    description: "Add or update the address box to your flyer design.",
    placement: "right",
    offset: 16,
    skipIf: notInDOM('[data-tour="address-box"]'),
  },
  {
    targetSelector: '[data-tour="background-colors"]',
    title: "Set Background Colors",
    description: "Choose background colors for the front and back of your flyer to match your brand identity.",
    placement: "right",
    offset: 16,
    skipIf: notInDOM('[data-tour="background-colors"]'),
    onExit: () => {
      // Close any open color picker popover
      window.dispatchEvent(new CustomEvent("tour:close-color-picker"));
    },
    advanceOn: {
      selector: "body",
      event: "tour:color-picked",
    },
  },
  {
    targetSelector: '[data-tour="units"]',
    title: "Set Units",
    description: "Select what unit type you'd like to use.",
    placement: "right",
    offset: 16,
    skipIf: notInDOM('[data-tour="units"]'),
    onExit: closeDropdown,
    advanceOn: { selector: "body", event: "tour:unit-selected" },
  },
  {
    targetSelector: '[data-tour="tool-popover"]',
    fallbackSelector: '[data-tour="tool-customRect"]',
    fallbackPlacement: "top",
    title: "Add Shapes",
    description: "Use shapes to highlight key info—like dates, offers, or directions—so they stand out in print.",
    placement: "top",
    offset: 12,
    highlightToolId: "customRect",
    skipIf: notInDOM('[data-tour="tool-customRect"]'),
    advanceOn: { selector: "body", event: "tour:selection-cleared" },
    hideWhileCreating: true,
    onEnter: () => {
      const shapesTool = document.querySelector('[data-tour="tool-customRect"]');
      const innerClickable = shapesTool?.querySelector("div > div") as HTMLElement;
      if (innerClickable) {
        innerClickable.click();
      } else if (shapesTool) {
        (shapesTool as HTMLElement).click();
      }
    },
    onExit: () => {
      closePopover();
      deselectAll();
    },
  },
  {
    targetSelector: '[data-tour="tool-popover"]',
    fallbackSelector: '[data-tour="tool-customImage"]',
    fallbackPlacement: "top",
    highlightToolId: "customImage",
    highlightDelay: 500,
    title: "Add Images",

    description: (
      <>
        Upload <strong style={{ fontWeight: 700 }}>your own images</strong> or{" "}
        <strong style={{ fontWeight: 700 }}>choose from Unsplash</strong> to make your poster more eye-catching in
        offline placements.
      </>
    ),
    placement: "top",
    offset: 12,
    skipIf: notInDOM('[data-tour="tool-customImage"]'),
    advanceOn: { selector: "body", event: "tour:selection-cleared" },
    hideWhileCreating: true,
    creatingSelector: '[data-tour="right-sidebar-content"]',
    onEnter: () => {
      // Small delay to let the previous popover close before opening this one
      setTimeout(() => {
        const imageTool = document.querySelector('[data-tour="tool-customImage"]');
        const innerClickable = imageTool?.querySelector("div > div") as HTMLElement;
        if (innerClickable) {
          innerClickable.click();
        } else if (imageTool) {
          (imageTool as HTMLElement).click();
        }
      }, 300);
    },
    onExit: () => {
      closePopover();
      deselectAll();
    },
  },
  {
    targetSelector: '[data-tour="tool-customText"]',
    highlightToolId: "customText",
    highlightDelay: 200,
    title: "Add Text",
    description: "Add text to your design.",
    placement: "top",
    offset: 16,
    skipIf: notInDOM('[data-tour="tool-customText"]'),
    advanceOn: { selector: "body", event: "tour:text-completed" },
    hideWhileCreating: true,
    onExit: deselectAll,
  },
  {
    targetSelector: '[data-tour="tool-customQRCode"]',
    highlightToolId: "customQRCode",
    highlightDelay: 200,
    title: "Add QR Code",
    description: "Enter a website link to generate your QR code, then adjust their size, color, and alignment.",
    placement: "top",
    offset: 16,
    skipIf: notInDOM('[data-tour="tool-customQRCode"]'),
    advanceOn: { selector: "body", event: "tour:selection-cleared" },
    hideWhileCreating: true,
    onExit: deselectAll,
  },
  {
    targetSelector: '[data-tour="tool-customIcons"]',
    highlightToolId: "customIcons",
    highlightDelay: 200,
    title: "Add Icons",
    description: "Use the search bar to find icons and add them to your design.",
    placement: "top",
    offset: 16,
    skipIf: notInDOM('[data-tour="tool-customIcons"]'),
    advanceOn: { selector: "body", event: "tour:selection-cleared" },
    hideWhileCreating: true,
    creatingSelector: '[data-tour="right-sidebar-content"]',
    onExit: deselectAll,
  },
  {
    targetSelector: '[data-tour="brandkit-panel"]',
    cardTargetSelector: '[data-tour="brandkit-title"]',
    title: "Add Your Brand Assets",
    description:
      "You can add your logo, brand colors, fonts, and images to ensure your design aligns with your brand identity.",
    placement: "right",
    offset: 16,
    onEnter: () => {
      const wrapper = document.querySelector('[data-tour="sidebar-brand_kit"]');
      const button =
        wrapper?.querySelector("button") ||
        wrapper?.querySelector("[role=button]") ||
        (wrapper?.firstElementChild as HTMLElement);
      if (button) (button as HTMLElement).click();
    },
  },
  {
    targetSelector: '[data-tour="variables-panel"]',
    cardTargetSelector: '[data-tour="variables-title"]',
    title: "Add Variables",
    description: (
      <>
        Add preset or custom Variables that personalize each poster.
        <br />
        <br />
        Ensure your CSV contains matching fields for automatic filling at print time.
      </>
    ),
    placement: "right",
    offset: 16,
    skipIf: notInDOM('[data-tour="sidebar-variables"]'),
    onEnter: () => {
      const wrapper = document.querySelector('[data-tour="sidebar-variables"]');
      const button =
        wrapper?.querySelector("button") ||
        wrapper?.querySelector("[role=button]") ||
        (wrapper?.firstElementChild as HTMLElement);
      if (button) (button as HTMLElement).click();
    },
  },
  {
    targetSelector: '[data-tour="layers-panel"]',
    cardTargetSelector: '[data-tour="layers-title"]',
    title: "Organize Your Layers",
    description: "Use the Layers panel to select, reorder, lock, or hide elements for easier editing.",
    placement: "right",
    offset: 16,
    onEnter: () => {
      const wrapper = document.querySelector('[data-tour="sidebar-layers"]');
      const button =
        wrapper?.querySelector("button") ||
        wrapper?.querySelector("[role=button]") ||
        (wrapper?.firstElementChild as HTMLElement);
      if (button) (button as HTMLElement).click();
    },
  },
];
